import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

/**
 * The global zone & corridor library, staff side (slice 24d).
 *
 * Types mirror the wire contract in `api/src/modules/zones/templateRoutes.ts`
 * (seam S1) and are never re-declared from the screen's point of view.
 */

export type AlertRuleId = 'never' | 'on_enter' | 'on_exit' | 'on_both';
export type ZonePurpose = 'keep_in' | 'no_go' | 'location' | 'poi';

export interface ZoneTemplate {
  id: string;
  name: string;
  city: string | null;
  purpose: ZonePurpose;
  kind: 'circle' | 'polygon' | 'corridor';
  suggestedAlertRule: AlertRuleId;
  corridorWidthM: number | null;
  notes: string | null;
  active: boolean;
  version: number;
  /** Composed server-side — the naming discipline lives in one place. */
  label: string;
}

export interface OutdatedCopy {
  geofenceId: string;
  geofenceName: string;
  tenantId: string;
  tenantName: string;
  copyVersion: number;
  templateVersion: number;
}

export const templatesQueryKey = ['admin', 'zone-templates'] as const;
export const outdatedQueryKey = ['admin', 'zone-templates', 'outdated'] as const;

export function useZoneTemplates(): UseQueryResult<{ templates: ZoneTemplate[] }> {
  return useQuery({
    queryKey: templatesQueryKey,
    queryFn: ({ signal }) =>
      apiJson<{ templates: ZoneTemplate[] }>('/api/admin/zone-templates', { signal }),
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

/**
 * Who is running a copy older than its template.
 *
 * 🔴 THE COST OF COPY-OVER-REFERENCE, RENDERED. It is empty today and will be
 * until template geometry becomes editable (slice 17d) — and it ships now
 * anyway, because the alternative to showing it is the divergence being
 * invisible, which is the strongest argument AGAINST the architecture that was
 * chosen.
 */
export function useOutdatedCopies(): UseQueryResult<{ copies: OutdatedCopy[] }> {
  return useQuery({
    queryKey: outdatedQueryKey,
    queryFn: ({ signal }) =>
      apiJson<{ copies: OutdatedCopy[] }>('/api/admin/zone-templates/outdated', { signal }),
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

export interface PublishTemplateBody {
  /**
   * 🔴 THE GEOMETRY COMES FROM AN EXISTING ZONE. There is no draw-a-template
   * path and no raw-GeoJSON path, deliberately — see the store's header. A
   * boundary invented rather than surveyed is a fabricated shape that mis-fires
   * alerts silently.
   */
  fromGeofenceId: string;
  name: string;
  city: string | null;
  purpose: ZonePurpose;
  suggestedAlertRule: AlertRuleId;
  notes: string | null;
}

export function usePublishTemplate(): UseMutationResult<
  ZoneTemplate,
  Error,
  PublishTemplateBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiJson<ZoneTemplate>('/api/admin/zone-templates/from-geofence', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: templatesQueryKey }),
  });
}

export function useUpdateTemplate(): UseMutationResult<
  ZoneTemplate,
  Error,
  { id: string; body: { active?: boolean; name?: string; city?: string | null } }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiJson<ZoneTemplate>(`/api/admin/zone-templates/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: templatesQueryKey }),
  });
}
